<template>
  <div id="contact-wizard-app" ref="hostElement">
    <Wizard ref="wizard" v-if="isReady"></Wizard>
    <div class="contact-blocker" v-if="!isReady"></div>
  </div>
</template>

<script>
import { Labels, Config, Utils, State, DataLayer, ServiceManager } from "./services";
import Wizard from "./components/Wizard.vue";
import { ref } from "vue";

export default {
  name: 'App',  //this is the Contact Wizard app. Not to be confused with the Contact Box app.
  components: {
    Wizard
  },
  data () {
    return {
      labels: null,
      dataLayer: null,
      config: null,
      utils: null,
      state: null,
      isReady: false
    }
  },
  setup() {
    const hostElement = ref(null);
    const uniqueId = Date.now().toString(36) + Math.random().toString(36).substring(2);
    return {
      hostElement,
      uniqueId
    }
  },
  created () {
    this.initServices();
  },
  mounted () {
    this.config.init(this.hostElement.parentNode.parentNode);
    this.state.init();
    this.labels.init(this.hostElement.parentNode.parentNode).then(res => {
      /*if (this.$refs.wizard) {
        console.log("Wizard exists...forcing label update.");
        this.$refs.wizard.$forceUpdate(); //we reload the wizard once we get the latest labels.
      } else {
        console.log("Wizard does not exist yet"); 
      }*/
    }).catch(err => {
      this.utils.showStatusMessage(`Could not load labels. Error: ${err.message}.`, "red");
    }).finally (() => {
      this.loadInitialState();
    });
  },
  methods: {
    initServices() {
      this.config = new Config();
      this.state = new State(this.config);
      this.utils = new Utils(this.state);
      this.dataLayer = new DataLayer(this.config, this.state, this.config);
      this.labels = new Labels(this.dataLayer, this.config);
      
      ServiceManager.addService(this, "Config", this.config);
      ServiceManager.addService(this, "Labels", this.labels);
      ServiceManager.addService(this, "DataLayer", this.dataLayer);
      ServiceManager.addService(this, "Utils", this.utils);
      ServiceManager.addService(this, "State", this.state);
    },
    loadInitialState() {
      this.state.current.bu = this.utils.toShortId(this.utils.getPageProperty("pageBuId"));
      this.state.current.bl = this.utils.toShortId(this.utils.getPageProperty("pageBlId"));
      this.state.current.currentPage = this.utils.toShortId(this.utils.getPageProperty("pageId"));
      if (this.utils.getPageProperty("hidProductName")) {
        //this is a product page
        this.state.current.currentProduct = this.state.current.currentPage;
      }
      this.state.current.country = this.utils.toShortId(this.utils.getCookie("CLACountry") || Config.defaultCountry);
      this.state.current.region = this.utils.toShortId(this.utils.getCookie("CLARegion") || Config.defaultRegion);
      console.log(`Current country: ${this.state.current.country}.`);
      console.log(`Current region: ${this.state.current.region}.`);

      let taxonomyPromise = this.dataLayer.loadTaxonomy();
      taxonomyPromise.then(res => {
        this.initTaxonomyMap(res.data);
      }).catch(err => {
        this.utils.showStatusMessage(`Could not load taxonomy. Error: ${err.message}.`, "red");
      });
      let blPromise = this.dataLayer.loadOrgUnits();
      blPromise.then(res => {
        this.state.current.blParents = res.data;
      }).catch(err => {
        this.utils.showStatusMessage(`Could not load the organizational unit structure. Error: ${err.message}.`, "red");
      });

      Promise.all([taxonomyPromise, blPromise]).finally(() => {
        this.isReady = true;
        this.state.current.isReady = true;
      });
    },
    initTaxonomyMap(data) {
      if (!data) return;
      this.state.current.taxonomy = data;
    }
  }
}
</script>

<style>
#contact-wizard-app {
  font-family: "Gotham SSm A", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
  min-height: 400px;
  position: relative;
  width: 100%;
}

.cla-messagebox {
  position: fixed;
  top: 100px;
  right: 0;
  background-color: #ffffff;
  max-width: 400px;
  padding: 20px;
  font-family: "Gotham SSm A", Arial, Helvetica, sans-serif;
  box-shadow: 3px 3px rgba(200, 200, 200, 0.4);
  border-left: 1px solid #e4e4e4;
  animation: slide 0.5s forwards;
  transition: opacity 500ms linear;
  opacity: 1;
  z-index: 10001;
}

@keyframes slide {
  0% {
    right: 0;
  }
  100% {
    right: 100px;
  }
}

.cla-messagebox.green {
  border-top: 3px solid green;
}

.cla-messagebox.yellow {
  border-top: 3px solid yellow;
}

.cla-messagebox.red {
  border-top: 3px solid red;
}

.loader {
  width: 64px;
  height: 64px;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg class='lds-spinner' width='64px' height='64px' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' viewBox='0 0 100 100' preserveAspectRatio='xMidYMid' style='background: none;'%3E%3Cg transform='rotate(0 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.9166666666666666s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(30 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.8333333333333334s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(60 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.75s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(90 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.6666666666666666s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(120 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.5833333333333334s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(150 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.5s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(180 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.4166666666666667s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(210 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.3333333333333333s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(240 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.25s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(270 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.16666666666666666s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(300 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.08333333333333333s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(330 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='0s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3C/svg%3E");
  margin: auto;
}

.loader.top {
  top: 80px;
  bottom: unset;
}

.spacer {
  flex-grow: 1;
}

.hr {
  height: 1px;
  background-color: #dddddd;
  background: linear-gradient(90deg, rgba(224,224,224,1) 0%, rgba(136,136,136,1) 49%, rgba(224,224,224,1) 100%);
  margin: 0 0 15px 0;
}

.step-heading {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 20px;
}

.contact-blocker {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.cla-textbox-container .input-container input {
  width: 100%;
}

</style>
